import {
  getFinderAgeBand,
  getFinderConditionCodes,
} from "../constants/cdmFinder";

const getCode = (condition) =>
  typeof condition === "string" ? condition : condition?.code;

// The manually-picked ICD-10 codes plus the codes seeded by the guided
// review-type/condition shortcut, de-duplicated by code (guided codes never
// clobber manual ones — they are merged).
const getMergedIcd10Codes = (filters = {}) => {
  const guided = getFinderConditionCodes(
    filters.finderReviewType,
    filters.finderConditions
  );
  const merged = [...(filters.icd10Codes || []), ...guided];

  const seen = new Set();
  return merged.filter((condition) => {
    const code = getCode(condition);
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
};

// The CDM finder is a targeted tool, not a browse-all report: with no condition
// and no medication criteria the backend would return every active non-enrolled
// patient, so the query is only fired once at least one criterion is chosen.
export const hasCdmFinderCriteria = (filters = {}) =>
  getMergedIcd10Codes(filters).length > 0 ||
  (filters.atcCodes?.length ?? 0) > 0;

// Builds the finder query suffix (prefixed with "&" so it appends after
// ?page=&rows=). Uses URLSearchParams so array criteria emit REPEATED keys
// (icd10Codes=E11&icd10Codes=I10) — getQueryParamsFromObject can't do that.
export const buildCdmFinderParams = (filters = {}) => {
  const params = new URLSearchParams();

  getMergedIcd10Codes(filters).forEach((condition) => {
    const code = getCode(condition);
    if (code) params.append("icd10Codes", code);
  });

  const atcCodes = filters.atcCodes || [];
  atcCodes.forEach((code) => code && params.append("atcCodes", code));

  // longTermRepeatOnly is only meaningful for a medication search. Send it
  // explicitly (default true) so an unchecked box is honoured rather than
  // silently dropped and re-defaulted to true by the backend.
  if (atcCodes.length > 0) {
    params.append("longTermRepeatOnly", String(filters.longTermRepeatOnly ?? true));
  }

  if (filters.patientFirstname) {
    params.append("patientFirstname", filters.patientFirstname);
  }
  if (filters.patientLastname) {
    params.append("patientLastname", filters.patientLastname);
  }
  if (filters.gender) params.append("gender", filters.gender);

  // Age band resolves to inclusive ageMin/ageMax (KIN-1409).
  const ageBand = getFinderAgeBand(filters.finderAgeBand);
  if (ageBand?.ageMin != null) params.append("ageMin", String(ageBand.ageMin));
  if (ageBand?.ageMax != null) params.append("ageMax", String(ageBand.ageMax));

  // Scheme filter — raw financial-class codes (01/02/04/05/06/07), repeated.
  (filters.financialClass || []).forEach(
    (code) => code && params.append("financialClass", code)
  );

  // Clinical / baseline dimensions (KIN-1409). Numbers are inclusive minimums.
  const appendMin = (key, value) => {
    const number = Number(value);
    if (value !== "" && value != null && Number.isFinite(number)) {
      params.append(key, String(number));
    }
  };

  appendMin("minSystolic", filters.minSystolic);
  appendMin("minDiastolic", filters.minDiastolic);
  appendMin("minBmi", filters.minBmi);

  // CKD Finder eGFR range (spec p.10, "enter eGFR value between X and Y").
  appendMin("minEgfr", filters.minEgfr);
  appendMin("maxEgfr", filters.maxEgfr);

  // Selections 3 & 4 (spec p.2): whether the patient is / is not already on the
  // Treatment / Prevention Programme. Two states only (On / Not on); the finder
  // defaults to "Not on" (a missing value is treated as "not"), so both are
  // always sent — the default excludes enrolled patients, preserving the
  // finder's purpose. BE contract: KIN-1448.
  params.append(
    "onTreatmentProgramme",
    String(filters.onTreatmentProgramme === "on")
  );
  params.append(
    "onPreventionProgramme",
    String(filters.onPreventionProgramme === "on")
  );

  // Booleans are only sent when on (the backend defaults cover the off case).
  if (filters.currentSmoker) params.append("currentSmoker", "true");
  if (filters.heartFailureMedCombo) {
    params.append("heartFailureMedCombo", "true");
  }
  if (filters.probableFirst) params.append("probableFirst", "true");

  // Private-insurer name filter (ANDs with the search), repeated.
  (filters.insurerNames || []).forEach(
    (name) => name && params.append("insurerNames", name)
  );

  const query = params.toString();
  return query ? `&${query}` : "";
};
